import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";

export default function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQty, subtotal, totalItems, checkout } = useCart();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Lock scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const shipping = subtotal >= 500 || subtotal === 0 ? 0 : 49;
  const total = subtotal + shipping;

  const handleCheckout = async () => {
    setCheckingOut(true);
    setCheckoutError(null);
    try {
      const checkoutUrl = await checkout();
      window.location.href = checkoutUrl;
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : "Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  // ... existing code ...

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[90] transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[100] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line text-amber-500 text-lg"></i>
            </div>
            <h2 className="font-extrabold text-gray-900 text-lg">Your Cart</h2>
            {totalItems > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer text-gray-500 hover:text-gray-900"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Free shipping progress */}
        {subtotal > 0 && subtotal < 500 && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-amber-700 font-medium">
                Add <span className="font-extrabold">${(500 - subtotal).toLocaleString()}</span> more for free shipping!
              </span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-truck-line text-amber-500"></i>
              </div>
            </div>
            <div className="h-1.5 bg-amber-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((subtotal / 500) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
        {subtotal >= 500 && subtotal > 0 && (
          <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2 text-emerald-700 text-xs font-semibold">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-truck-line"></i>
            </div>
            You qualify for Free Shipping!
          </div>
        )}

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-50 rounded-full">
                <i className="ri-shopping-cart-line text-4xl text-gray-300"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-700 text-base">Your cart is empty</p>
                <p className="text-sm text-gray-400 mt-1">Add some products to get started</p>
              </div>
              <button
                onClick={() => { closeCart(); navigate("/shop"); }}
                className="mt-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Products
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 group">
                  {/* Image */}
                  <div
                    className="w-20 h-20 shrink-0 bg-gray-50 rounded-lg overflow-hidden cursor-pointer"
                    onClick={() => { closeCart(); navigate(`/shop/${item.id}`); }}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-0.5">{item.brand}</p>
                    <p
                      className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 cursor-pointer hover:text-amber-600 transition-colors"
                      onClick={() => { closeCart(); navigate(`/shop/${item.id}`); }}
                    >
                      {item.name}
                    </p>
                    <p className="text-amber-600 font-extrabold text-sm mt-1">
                      ${(item.price * item.quantity).toLocaleString()}
                    </p>

                    {/* Qty controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
                        <button
                          onClick={() => updateQty(item.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer text-sm"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="w-8 text-center text-sm font-bold text-gray-900 select-none">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQty(item.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer text-sm"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors cursor-pointer ml-1 opacity-0 group-hover:opacity-100"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-100 px-5 py-5 flex flex-col gap-3 bg-white">
            {/* Subtotal */}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Subtotal ({totalItems} {totalItems === 1 ? "item" : "items"})</span>
              <span className="font-semibold text-gray-900">${subtotal.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Shipping</span>
              {shipping === 0 ? (
                <span className="text-emerald-600 font-semibold">FREE</span>
              ) : (
                <span className="font-semibold text-gray-900">${shipping}</span>
              )}
            </div>
            <div className="flex items-center justify-between font-extrabold text-gray-900 text-base border-t border-gray-100 pt-3">
              <span>Total</span>
              <span>${total.toLocaleString()}</span>
            </div>

            {/* Checkout error */}
            {checkoutError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-xs text-red-700">
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-500"></i>
                </div>
                <span>{checkoutError}</span>
              </div>
            )}

            {/* Checkout */}
            <button
              onClick={handleCheckout}
              disabled={checkingOut}
              className="w-full py-4 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-extrabold text-base rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {checkingOut ? (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin"></i>
                  </div>
                  Creating Checkout…
                </>
              ) : (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-line"></i>
                  </div>
                  Secure Checkout
                </>
              )}
            </button>

            {/* Trust line */}
            <p className="text-center text-xs text-gray-400">
              <i className="ri-shield-check-line text-emerald-500 mr-1"></i>
              SSL Encrypted &nbsp;•&nbsp; Free Returns &nbsp;•&nbsp; Expert Support
            </p>

            {/* Continue shopping */}
            <button
              onClick={closeCart}
              className="text-xs text-gray-400 hover:text-amber-600 transition-colors cursor-pointer text-center"
            >
              Continue Shopping &rarr;
            </button>
          </div>
        )}
      </div>
    </>
  );
}
